import {
  Component,
  EventEmitter,
  Input,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';

import { NG_VALUE_ACCESSOR, Validators } from '@angular/forms';
import { NgSelectComponent } from '@ng-select/ng-select';
import { isNullOrUndefined } from '@swimlane/ngx-datatable';
import {
  DropDownOption,
  OrderByFn,
} from 'src/app/product-capture/model/interfaces/drop-down-option.interface';
import { ControlValueAccessorConnector } from '../controlvalue-accessor.connector';

@Component({
  selector: 'app-dc-select',
  templateUrl: './dc-select.component.html',
  styleUrls: ['./dc-select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: DcSelectComponent,
      multi: true,
    },
  ],
})
export class DcSelectComponent extends ControlValueAccessorConnector {
  @Input() label: string;
  @Input() warning: string;
  @Input() helpText?: string;
  @Input() labelId: string;
  @Input() multiple: boolean;
  @Input() searchable: boolean;
  @Input() closeOnSelect: boolean;
  @Input() items: any[];
  @Input() checkbox: boolean;
  @Input() placeholder: string;
  @Input() darkMode?: boolean;
  @Input() loading: boolean;
  @Input() FreeText: boolean;
  @Input() showTicksIfValid: boolean; // off by default
  @Input() customErrorMessage: string;
  @Input() editButton: boolean;
  @Input() clearSelection: boolean;
  @Input() clearInputX: boolean;
  @Input() dropdownParent: string;
  @Input() notFoundText = 'No Results Found';
  @Input() orderBy: string | OrderByFn;
  @Input() orderDirection: 'asc' | 'desc' = 'asc';
  @Input() orderByProperty: string;
  @Input() compareWith?: boolean;
  @Input() splitLabel: boolean;
  @Input() virtualScroll: boolean;
  @Input() typeToSearchText: string;
  @Input() clearSearchOnAdd: boolean;
  @Input() showRequiredLabel: boolean = false;
  @Output() disableEvent = new EventEmitter<any>();
  @Output() selected = new EventEmitter<any>();
  @Output() editEvent = new EventEmitter<any>();
  @Output() optionClicked = new EventEmitter<any>();

  @ViewChild(NgSelectComponent) ngSelectComponent: NgSelectComponent;

  ngOnInit(): void {
    this.addCustomItem();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ('orderBy' in changes) {
      this.orderItems();
    }

    super.ngOnChanges(changes);
  }

  orderItems(): void {
    if (!this.orderBy) {
      return;
    }

    let orderByFn: (a: DropDownOption, b: DropDownOption) => number;
    if (typeof this.orderBy === 'string') {
      orderByFn = (a: DropDownOption, b: DropDownOption) => {
        const orderProperty = this.orderByProperty || 'id';
        if (this.orderDirection === 'asc') {
          return a[orderProperty] > b[orderProperty] ? 1 : -1;
        } else {
          return a[orderProperty] < b[orderProperty] ? 1 : -1;
        }
      };
    }

    if (!orderByFn && typeof this.orderBy === 'function') {
      orderByFn = this.orderBy;
    }

    if (orderByFn) {
      this.items = this.items.sort(orderByFn);
    }
  }

  onChange(e): void {
    if (e instanceof Event) {
      return;
    }

    if (e !== undefined) {
      this.control.markAsTouched();
      this.selected.emit(e.id);
      this.addCustomItem();
    }

    if (Array.isArray(e)) {
      const item = e?.map((item) => item.id);
      this.control.setValue(item, { emitEvent: false });
    }
  }

  clearSelectionFn() {
    this.ngSelectComponent.handleClearClick();
    this.ngSelectComponent.isOpen = false;
    this.selected.emit(null);
  }

  addCustomItem(): void {
    const { value } = this.control;

    if (typeof value === 'string' && this.items) {
      if (!this.items.find((item) => item.id === value)) {
        this.items = [
          ...this.items,
          {
            id: value,
            displayValue: value,
          },
        ];
        this.orderItems();
      }
    }
  }

  writeValue(obj: any): void {
    if (
      !isNullOrUndefined(obj) &&
      typeof obj === 'string' &&
      obj.trim().length > 0
    ) {
      this.addCustomItem();
    }

    this.stripEmptyValues(obj);

    super.writeValue(obj);
  }

  getValidationStatus(): string {
    return this.control.invalid
      ? 'invalid'
      : this.showTicksIfValid
      ? 'valid'
      : '';
  }

  onOpen(component: NgSelectComponent): void {
    setTimeout(() => {
      const parent = component.dropdownPanel.contentElementRef.nativeElement.parentElement.parentElement as HTMLElement;
      parent.classList.add(this.checkbox ? 'withCheckbox' : 'withoutCheckbox');
    })
  }

  addTagFn(input) {
    return { displayValue: input, id: input };
  }

  edit() {
    this.editEvent.emit();
  }

  detectChanges(): void {
    this.cdr.detectChanges();
  }

  compareFn(item, selected) {
    const returnValue =
      typeof selected === 'string'
        ? true
        : typeof selected === 'number'
        ? item.id === selected
        : item.displayValue === selected.name;
    return returnValue;
  }

  onOptionClicked(item: any) {
    this.optionClicked.emit(item);
  }

  stripEmptyValues(obj: any): void {
    if (this.multiple && Array.isArray(obj)) {
      let filtered = obj.filter((item) => item !== '');
      if (filtered.length !== obj.length) {
        setTimeout(() => {
          this.control.setValue(obj.filter((item) => item !== ''));
        });
      }
    }
  }

  canShowRequiredLabel(): boolean {
    return this.showRequiredLabel && this.control.hasValidator(Validators.required);
  }
}
